import { useState, useEffect, useMemo } from 'react';
import logger from '@gral/log';

import { CEvent, convertFromMilliseconds } from '@ts/ui-models';

import { KPI_LATEST_EXECUTIONS_URL, KPI_EXECUTION_RESULTS_URL, KPI_LIST_EXECUTIONS_URL } from './constants';

import { useClassSelector } from '@redux/store';
import { CKPI } from '@ts/ui-models';
import { IConfigComponent } from '@ts/ui-models';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  generateGlobalColumns,
  generateBasicColumn,
  generateDateColumn,
  generateEntityColumn,
  generateMultiEntityColumn,
  generateNumberColumn,
  IMultiEntitySelectorCellProps,
} from '@ts/storybook';
import { CElement } from '@ts/ui-models';
import { CEntity } from '@ts/ui-models';
import { IEntity } from '@ts/ui-models';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import actStore from '@flux/actions/actStore';

interface IKpiResultProps {
  executionId?: string;
  title?: string;
  where?: object;
  downloadOptions?: any;
  fnChange?: any;
  quickFindInit?: any;
  isEntitySelector?: boolean;
}

interface KPIResultsState {
  executionId?: string;
}

const log = logger('KPIResults');

const KpiResults = (props: IKpiResultProps) => {
  const kpiSelected = useClassSelector(state => state.KPI.kpiSelected, CKPI);

  const [state, setState] = useState<KPIResultsState>({
    executionId: props.executionId,
  });

  const configComponent: IConfigComponent = useMemo(
    () => ({
      componentId: 'kpiMetrics',
      aclId: 'KpiRepDef',
      fetchURL: kpiSelected?.getID()
        ? props.executionId
          ? KPI_EXECUTION_RESULTS_URL(kpiSelected?.getID(), state.executionId)
          : KPI_LATEST_EXECUTIONS_URL(kpiSelected?.getID())
        : KPI_LIST_EXECUTIONS_URL('undefined'),
    }),
    [kpiSelected]
  );

  //#region Functions
  /**
   * Returns an array of columns based on the value of `state.kpiOperationType`.
   * @returns {Array} An array of columns.
   */
  const getColumnsByType = () => {
    let dynamicColumnsNames = {};
    switch (kpiSelected?.getKPIType) {
      case 'inter_event_item_usage':
        dynamicColumnsNames['startEvent'] = kpiSelected?.kpiOperation.data?.startEventType?.eventType?.name ?? 'Start';
        dynamicColumnsNames['endEvent'] = kpiSelected?.kpiOperation.data?.endEventType?.eventType?.name ?? 'End';
        return COLUMNS_KPIInterEventItemUsageResult(dynamicColumnsNames);
      case 'time_between_events':
        dynamicColumnsNames['startEvent'] = kpiSelected?.kpiOperation.data?.startEventType?.eventType?.name ?? 'Start';
        dynamicColumnsNames['endEvent'] = kpiSelected?.kpiOperation.data?.endEventType?.eventType?.name ?? 'End';
        return COLUMNS_KPITimeBetweenEventResult(dynamicColumnsNames);
      case 'count_events':
        dynamicColumnsNames['eventType'] = kpiSelected?.kpiOperation?.data?.eventType?.eventType?.name ?? 'Count';
        return COLUMNS_KPICountEventsResult(dynamicColumnsNames);
      case 'value_between_readings':
        return COLUMNS_KPIValueBetweenReadingsResult(dynamicColumnsNames);
      case 'interval_summation':
        return COLUMNS_KPIIntervalSummationResult(dynamicColumnsNames);
      default:
        return [];
    }
  };

  const onClickMultiEntityCell = (props: IMultiEntitySelectorCellProps, listIds: string[]) => {
    const { params } = props;
    const { optionsColumn } = props.options;
    const event = generateSearchEntitySelector('event');

    actStore.setActionTableV3({
      title: optionsColumn?.headerName + (params.row.getName() !== 'Unknown' ? ' - ' + params.row.getName() : ''),
      action: 'openModalExtraComp',
      component: event.component,
      propsComponent: { configComponent: { ...event.configComponent, where: { logicalId: listIds } } },
      row: params.row,
    });
  };

  const generateColumns = (): GridColDef[] => {
    const mixProps = { ...props, configComponent: configComponent };
    const globalCells = generateGlobalColumns(mixProps);
    let listColumns: ColumnsProps[] = getColumnsByType();

    const newListColumns = listColumns.map((column: ColumnsProps) => {
      switch (column.type) {
        case 'entityCell':
          return generateEntityColumn(
            { field: column.value, headerName: column.label },
            { field: column.value, mainClass: column.tClass }
          );
        case 'multiEntityCell':
          return generateMultiEntityColumn(
            { field: column.value, headerName: column.label },
            { field: column.value, mainClass: column.tClass },
            { onClick: onClickMultiEntityCell }
          );
        case 'string':
          return generateBasicColumn(
            { field: column.value, headerName: column.label, dataFormat: column.dataFormat },
            { field: column.value, mainClass: column.tClass }
          );
        case 'dateTime':
          return generateDateColumn(
            { field: column.value, headerName: column.label },
            { field: column.value, mainClass: column.tClass },
            {
              condition: column.condition,
              optionalLabel: column.optionalLabel,
            }
          );
        case 'number':
          return generateNumberColumn(
            { field: column.value, headerName: column.label },
            { field: column.value, mainClass: column.tClass }
          );
      }
    });

    return [globalCells.id()].concat(newListColumns);
  };

  /*   const _getDownloadOptions = () => {
    const listColumns = getColumnsByType();
    return {
      enable: true,
      fields: listColumns.map(col => {
        return { label: col.labelDownload || col.label, value: row => getValue(fromJS(row), col, undefined, true).value };
      }),
    };
  }; */
  //#endregion

  //#region Renders

  //#endregion

  useEffect(() => {
    if (kpiSelected) {
      setState({ ...state, executionId: props.executionId });
    }
  }, [props.executionId]);

  useEffect(() => {
    if (kpiSelected) {
      log.info('Mounting componentID -', kpiSelected);
      log.info('KPI Operation Type -', kpiSelected.getKPIType);
    }
  }, [kpiSelected]);

  if (!kpiSelected) return;

  return (
    <WrapperDataGrid
      key={kpiSelected.getID()}
      //title={kpiSelected.getName()}
      configComponent={configComponent}
      columns={generateColumns()}
      withoutActions
    />
  );
};

export default KpiResults;

interface ColumnsProps {
  id: string;
  label: string; //Column head
  labelDownload?: string; //Column head for csv download
  value: string; //Field to show get value
  type: 'entityCell' | 'multiEntityCell' | 'dateTime' | 'number' | 'string';
  dataFormat?: (v: any) => any; //Function to format the value

  condition?: string; //Field to check value
  optionalLabel?: string; //Label to see if condition is true
  tClass?: new (data: any) => CEntity<IEntity>; //Class to transform
}

const COLUMNS_KPIInterEventItemUsageResult = (dynamicColumnsNames: any): ColumnsProps[] => [
  {
    id: 'itemName',
    label: 'Name',
    value: 'item',
    type: 'entityCell',
    tClass: CElement,
  },
  {
    id: 'startEventTimestamp',
    label: `${dynamicColumnsNames?.startEvent} (time)`,
    value: 'startEvent.timestamp',
    type: 'dateTime',
  },
  {
    id: 'startEventLocationName',
    label: `${dynamicColumnsNames?.startEvent} (location)`,
    value: 'startEvent.location',
    type: 'entityCell',
  },
  {
    id: 'endEventTimestamp',
    label: `${dynamicColumnsNames?.endEvent} (time)`,
    value: 'endEvent.timestamp',
    condition: 'isInProgress',
    optionalLabel: 'In progress',
    type: 'dateTime',
  },
  {
    id: 'endEventLocationName',
    label: `${dynamicColumnsNames?.endEvent} (location)`,
    value: 'endEvent.location',
    condition: 'isInProgress',
    type: 'entityCell',
  },
  {
    id: 'duration',
    label: 'Duration',
    labelDownload: 'Duration (ms)',
    value: 'duration',
    dataFormat: v => convertFromMilliseconds(v, 'string'),
    condition: 'isInProgress',
    type: 'string',
  },
  {
    id: 'utilizationDays',
    label: 'Billable days',
    value: 'utilizationDays',
    condition: 'isInProgress',
    type: 'string',
  },
];

const COLUMNS_KPITimeBetweenEventResult = (dynamicColumnsNames: any): ColumnsProps[] => [
  {
    id: 'itemName',
    label: 'Name',
    value: 'item',
    type: 'entityCell',
    tClass: CElement,
  },
  {
    id: 'startEventTimestamp',
    label: `${dynamicColumnsNames?.startEvent} (time)`,
    value: 'startEvent.timestamp',
    type: 'dateTime',
  },
  {
    id: 'startEventLocationName',
    label: `${dynamicColumnsNames?.startEvent} (location)`,
    value: 'startEvent.location',
    type: 'entityCell',
  },
  {
    id: 'endEventTimestamp',
    label: `${dynamicColumnsNames?.endEvent} (time)`,
    value: 'endEvent.timestamp',
    condition: 'isInProgress',
    optionalLabel: 'In progress',
    type: 'dateTime',
  },
  {
    id: 'endEventLocationName',
    label: `${dynamicColumnsNames?.endEvent} (location)`,
    value: 'endEvent.location',
    condition: 'isInProgress',
    type: 'entityCell',
  },
  {
    id: 'duration',
    label: 'Duration',
    labelDownload: 'Duration (ms)',
    value: 'duration',
    dataFormat: v => convertFromMilliseconds(v, 'string'),
    condition: 'isInProgress',
    type: 'string',
  },
];

const COLUMNS_KPICountEventsResult = (dynamicColumnsNames: any): ColumnsProps[] => [
  {
    id: 'itemName',
    label: 'Name',
    value: 'item',
    type: 'entityCell',
    tClass: CElement,
  },
  {
    id: 'timestamp',
    label: `Date`,
    value: 'timestamp',
    type: 'dateTime',
  },
  {
    id: 'count',
    label: `${dynamicColumnsNames?.eventType}`,
    value: 'count',
    type: 'number',
  },
  {
    id: 'geometry',
    label: `Geometry`,
    value: 'geometry',
    type: 'entityCell',
  },
];

const COLUMNS_KPIValueBetweenReadingsResult = (dynamicColumnsNames: any): ColumnsProps[] => [
  {
    id: 'itemName',
    label: 'Name',
    value: 'item',
    type: 'entityCell',
    tClass: CElement,
  },
  {
    id: 'initialtimestamp',
    label: `Initial time`,
    value: 'initialReading.timestamp',
    type: 'dateTime',
  },
  {
    id: 'initialvalue',
    label: `Initial value`,
    value: 'initialReading.data',
    type: 'number',
  },
  {
    id: 'finaltimestamp',
    label: `Final time`,
    value: 'finalReading.timestamp',
    type: 'dateTime',
  },
  {
    id: 'finalvalue',
    label: `Final  value`,
    value: 'finalReading.value',
    type: 'number',
  },
];

const COLUMNS_KPIIntervalSummationResult = (dynamicColumnsNames: any): ColumnsProps[] => [
  {
    id: 'itemName',
    label: 'Name',
    value: 'item',
    type: 'entityCell',
    tClass: CElement,
  },
  {
    id: 'duration',
    label: `Duration`,
    value: 'duration',
    type: 'string',
    dataFormat: v => convertFromMilliseconds(v, 'string'),
  },
  {
    id: 'events',
    label: 'Events',
    value: 'events',
    type: 'multiEntityCell',
    tClass: CEvent,
  },
];
